import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SnookerScoringNotice from '../Components/SnookerScoringNotice';
import SnookerRedsControls from '../Components/SnookerRedsControls';
import StreamingStatsControls from '../Components/StreamingStatsControls';
import { freeBallTarget, snookerPointsRemaining } from '../utils/snookerScoring';
import useRefereeTheme from '../hooks/useRefereeTheme';
import './SnookerRefereeDesk.css';

const BALLS = [
    ['Red', 1, '#d74850'], ['Yellow', 2, '#edc74b'], ['Green', 3, '#388b62'],
    ['Brown', 4, '#956246'], ['Blue', 5, '#438acd'], ['Pink', 6, '#d67caa'], ['Black', 7, '#202526'],
];

function Icon({ name, ...props }) {
    const paths = {
        switch: <><path d="M4 7h15l-4-4M20 17H5l4 4" /><path d="M19 7l-4 4M5 17l4-4" /></>,
        undo: <><path d="M9 5L4 10l5 5M4 10h9a6 6 0 0 1 6 6v3" /></>,
        flag: <><path d="M5 21V4m0 0c5-5 9 5 15 0v10c-6 5-10-5-15 0" /></>,
        expand: <path d="M9 3H3v6m12-6h6v6M3 15v6h6m12-6v6h-6" />,
        reset: <><path d="M3 10a9 9 0 1 1 2 8M3 3v7h7" /></>,
        clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
        sync: <><path d="M20 8a8 8 0 0 0-14-3L3 8m0-5v5h5M4 16a8 8 0 0 0 14 3l3-3m0 5v-5h-5" /></>,
        play: <path d="m9 5 11 7-11 7z" />,
        pause: <><path d="M8 5v14M16 5v14" /></>,
        foul: <><path d="m12 3 10 18H2L12 3zM12 9v5M12 17v.1" /></>,
        ball: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" /></>,
        sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" /></>,
        moon: <path d="M21 13a9 9 0 0 1-10-10 9 9 0 1 0 10 10Z" />,
    };
    return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>{paths[name]}</svg>;
}

function Confirmation({ action, onCancel, onConfirm, theme }) {
    const dialogRef = useRef(null);
    const cancelRef = useRef(null);
    useEffect(() => {
        const previousFocus = document.activeElement;
        cancelRef.current?.focus();
        const onKey = event => {
            if (event.key === 'Escape') { event.preventDefault(); onCancel(); }
            if (event.key === 'Tab') {
                const buttons = [...dialogRef.current.querySelectorAll('button:not(:disabled)')];
                const first = buttons[0], last = buttons[buttons.length - 1];
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
                if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('keydown', onKey); previousFocus?.focus(); };
    }, [onCancel]);
    const copy = {
        winFrame: ['Finish this frame?', 'Record the frame result and move the match forward.', 'Confirm frame'],
        resetFrames: ['Reset the entire match?', 'This clears both scores and all frame counts. Only reset when restarting the match.', 'Reset all frames'],
        resetTimer: ['Reset the frame clock?', 'The elapsed frame time will return to zero. Scores will stay unchanged.', 'Reset clock'],
    }[action];
    return createPortal(
        <div className="ref-desk ref-confirm-backdrop" data-ref-theme={theme}>
            <section ref={dialogRef} className="ref-confirm" role="alertdialog" aria-modal="true"
                aria-labelledby="ref-confirm-title" aria-describedby="ref-confirm-description" data-referee-dialog>
                <span className="ref-eyebrow">PLEASE CONFIRM</span>
                <h2 id="ref-confirm-title">{copy[0]}</h2>
                <p id="ref-confirm-description">{copy[1]}</p>
                <div className="ref-confirm-actions">
                    <button ref={cancelRef} onClick={onCancel}>Cancel</button>
                    <button className={action === 'resetFrames' ? 'ref-button-danger' : 'ref-button-primary'} onClick={onConfirm}>{copy[2]}</button>
                </div>
            </section>
        </div>, document.body,
    );
}

export default function SnookerRefereeDesk({
    gameState, game, settings = {}, actionStack = [], potRed, potColor, switchPlayer,
    addFoul, freeBall, addFoulPoints, potFreeBall, cancelScoringMode, undoAction,
    adjustReds, winFrame, resetFrames, startTimer, pauseTimer, resetTimer,
    startShotTimer, stopShotTimer, resetShotTimer, formatTime, formatShotTime,
    calculateCurrentTime, calculateShotRemaining, getFlagComponent, forceSync,
    controlStreamingStats, toggleFullScreen, refereeConfirmationRef,
}) {
    const { theme, toggleTheme } = useRefereeTheme();
    const [confirmation, setConfirmation] = useState(null);
    const closed = gameState.frameOver || gameState.isGameFinished;
    const choosing = gameState.selectingFoulValue || gameState.freeBallActive;
    const shotClockEnabled = settings.snooker_shot_timer_enabled === 'true';
    const activePlayer = gameState.currentPlayer === 2 ? gameState.secondPlayer : gameState.firstPlayer;
    const frameTime = formatTime(calculateCurrentTime());
    const remaining = snookerPointsRemaining(gameState);
    const closeConfirmation = React.useCallback(() => setConfirmation(null), []);
    useEffect(() => {
        if (!refereeConfirmationRef) return;
        refereeConfirmationRef.current = action => {
            if (action === 'winFrame' && (closed || choosing)) return;
            if (action === 'resetTimer' && closed) return;
            setConfirmation(action);
        };
        return () => { refereeConfirmationRef.current = null; };
    }, [refereeConfirmationRef, closed, choosing]);
    const confirmAction = () => {
        const handlers = { winFrame, resetFrames, resetTimer };
        handlers[confirmation]?.();
        setConfirmation(null);
    };
    const player = (number) => {
        const active = Number(gameState.currentPlayer) === number && !closed;
        const name = number === 1 ? gameState.firstPlayer : gameState.secondPlayer;
        const nationality = number === 1 ? gameState.firstPlayerNationality : gameState.secondPlayerNationality;
        return (
            <div className={`ref-player ref-player-${number}${active ? ' is-active' : ''}`} aria-label={`Player ${number}: ${name}`}>
                <div className="ref-player-top">
                    <span className="ref-player-name">
                        {String(game?.has_flags) === '1' && <span className="ref-player-flag">{getFlagComponent(nationality)}</span>}
                        <span>{name}</span>
                    </span>
                    <span className={`ref-turn-label ${active ? '' : 'is-waiting'}`}>{active ? 'AT TABLE' : 'PLAYER ' + number}</span>
                </div>
                <div className="ref-player-bottom">
                    <strong className="ref-player-score">{gameState[`player${number}Score`]}</strong>
                    <span className="ref-player-break">Break <b>{gameState[`player${number}Break`] || 0}</b></span>
                </div>
            </div>
        );
    };

    return (
        <main className="ref-desk" data-ref-theme={theme}>
            <div className="ref-shell">
                <header className="ref-topbar">
                    <div className="ref-brand">
                        <span className="ref-brand-mark" aria-hidden="true">8</span>
                        <div><span className="ref-eyebrow">CUECLUB MANAGER</span><h1>Referee desk</h1></div>
                    </div>
                    <div className="ref-topbar-meta">
                        <span className="ref-match-name">{game?.name || 'Snooker match'}</span>
                        <button className="ref-theme-toggle" role="switch" aria-label="Dark mode"
                            aria-checked={theme === 'dark'} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                            onClick={toggleTheme}>
                            <Icon name={theme === 'dark' ? 'moon' : 'sun'} />
                            <span>{theme === 'dark' ? 'Dark' : 'Light'}</span>
                            <span className="ref-theme-track" aria-hidden="true"><span /></span>
                        </button>
                        {toggleFullScreen && <button className="ref-icon-button" aria-label="Toggle fullscreen" onClick={toggleFullScreen}><Icon name="expand" /></button>}
                    </div>
                </header>

                <section className="ref-match" aria-label="Live match score">
                    <div className="ref-match-players">
                        {player(1)}
                        <div className="ref-frames">
                            <span className="ref-eyebrow">FRAMES</span>
                            <strong>{gameState.player1FramesWon}<span>—</span>{gameState.player2FramesWon}</strong>
                            <span>{[1, '1', true].includes(game?.is_race) && game?.race_to ? `Race to ${game.race_to}` : `Frame ${gameState.currentFrame || 1}`}</span>
                        </div>
                        {player(2)}
                    </div>
                    <div className="ref-match-footer">
                        <span><i aria-hidden="true" />{closed ? 'Frame complete' : `Frame ${gameState.currentFrame || 1} in progress`}</span>
                        <span><b>{remaining}</b> points remaining</span>
                    </div>
                </section>

                {gameState.isGameFinished && <div className="ref-complete" role="status">{gameState.winMessage || 'Match complete'}</div>}

                <div className="ref-workspace">
                    <div className="ref-primary-column">
                        <section className="ref-panel ref-scoring" aria-labelledby="ref-scoring-title">
                            <div className="ref-section-heading">
                                <div><span className="ref-eyebrow">SCORING</span><h2 id="ref-scoring-title">{choosing ? 'Referee decision' : 'Score a ball'}</h2></div>
                                <span className="ref-scoring-for">{closed ? 'Scoring closed' : `Scoring for ${activePlayer}`}</span>
                            </div>
                            <div className="ref-score-entry">
                                {choosing && !closed
                                    ? <SnookerScoringNotice gameState={gameState} addFoulPoints={addFoulPoints} potFreeBall={potFreeBall} cancelScoringMode={cancelScoringMode} />
                                    : <div className="ref-ball-grid">
                                        {BALLS.map(([name, value, colour]) => (
                                            <button key={name} className={`ref-ball-button ref-ball-${name.toLowerCase()}`}
                                                style={{ '--ball-color': colour }}
                                                aria-label={`Pot ${name.toLowerCase()} (${value} ${value === 1 ? 'point' : 'points'})`}
                                                disabled={closed || (name === 'Red' ? gameState.redsRemaining === 0 : gameState.finalColorsPhase && gameState.colorsPottedFirstTime?.[name])}
                                                onClick={() => name === 'Red' ? potRed() : potColor(name, value)}>
                                                <span className="ref-ball" aria-hidden="true"><span>{value}</span></span>
                                                <span className="ref-ball-label">{name}<small>+{value} {value === 1 ? 'point' : 'points'}</small></span>
                                            </button>
                                        ))}
                                    </div>}
                            </div>
                            <div className="ref-play-actions">
                                <button className="ref-switch" aria-label="Switch" disabled={closed || choosing} onClick={switchPlayer}><Icon name="switch" /><span>Switch player</span></button>
                                <button className="ref-foul" aria-label="Foul" aria-pressed={!!gameState.selectingFoulValue} disabled={closed} onClick={addFoul}><Icon name="foul" /><span>Foul</span></button>
                                <button className="ref-free" aria-label="Free ball" aria-pressed={!!gameState.freeBallActive} disabled={closed || !freeBallTarget(gameState)} onClick={freeBall}><Icon name="ball" /><span>Free ball</span></button>
                                <button aria-label="Undo" disabled={!actionStack.length && !choosing} onClick={undoAction}><Icon name="undo" /><span>Undo</span></button>
                            </div>
                            <p className="ref-keyboard-hint">Keyboard: <kbd>1</kbd>–<kbd>7</kbd> score a ball <span>·</span> <kbd>F</kbd> foul <span>·</span> <kbd>B</kbd> free ball <span>·</span> <kbd>8</kbd> undo</p>
                        </section>

                        <section className="ref-panel ref-clocks" aria-label="Match clocks">
                            <div className="ref-clock-row">
                                <div className="ref-clock-reading"><span className="ref-eyebrow"><Icon name="clock" /> FRAME CLOCK</span><strong>{frameTime}</strong></div>
                                <div className="ref-clock-actions">
                                    <button className="ref-button-primary" onClick={gameState.timerIsRunning ? pauseTimer : startTimer} disabled={closed}>
                                        <Icon name={gameState.timerIsRunning ? 'pause' : 'play'} />{gameState.timerIsRunning ? 'Pause timer' : 'Start timer'}
                                    </button>
                                    <button aria-label="Reset frame clock" className="ref-icon-button" disabled={closed || calculateCurrentTime() === 0} onClick={() => setConfirmation('resetTimer')}><Icon name="reset" /></button>
                                </div>
                            </div>
                            {shotClockEnabled && <div className="ref-clock-row ref-shot-clock" aria-label="Shot clock">
                                <div className="ref-clock-reading"><span className="ref-eyebrow">SHOT CLOCK</span><strong>{formatShotTime(calculateShotRemaining())}</strong></div>
                                <div className="ref-clock-actions">
                                    <button onClick={gameState.shotTimerIsRunning ? stopShotTimer : startShotTimer} disabled={closed}>{gameState.shotTimerIsRunning ? 'Stop shot' : 'Start shot'}</button>
                                    <button onClick={resetShotTimer} disabled={closed}>Reset shot</button>
                                </div>
                            </div>}
                        </section>
                        <div className="ref-match-actions">
                            <button className="ref-finish" disabled={closed || choosing} onClick={() => setConfirmation('winFrame')}><Icon name="flag" />Finish frame</button>
                            <details className="ref-more-actions">
                                <summary>Match options</summary>
                                <button className="ref-reset-match" onClick={() => setConfirmation('resetFrames')}><Icon name="reset" />Reset frames</button>
                            </details>
                        </div>
                    </div>

                    <aside className="ref-secondary-column" aria-label="Table and broadcast controls">
                        <SnookerRedsControls gameState={gameState} adjustReds={adjustReds} />
                        <StreamingStatsControls gameState={gameState} onCommand={controlStreamingStats} />
                        <div className="ref-sync-tools">
                            <span>Display not updating?</span>
                            <button aria-label="Force sync all devices" onClick={forceSync}><Icon name="sync" />Sync displays</button>
                        </div>
                    </aside>
                </div>
                <footer className="ref-footer"><span>REFEREE CONTROLS</span><span>Scores and corrections are shared with connected displays.</span></footer>
            </div>
            {confirmation && <Confirmation action={confirmation} onCancel={closeConfirmation} onConfirm={confirmAction} theme={theme} />}
        </main>
    );
}
